import { useMemo } from 'react';
import { NotificationStatus } from 'src/api/getNotifications/getNotifications';
import { findProductSyncNotification, isIngestSyncNotification } from 'src/helpers/isIngestSync/isIngestSync';
import { useNotifications } from 'src/hooks/useNotifications/useNotifications';

export interface IIngestSyncRouting {
  isIngestSync: boolean;
  /**
   * True while the notifications that carry the discriminator are still loading. Both review modals
   * fail loudly against the wrong pipeline, so callers should wait rather than guess.
   */
  isResolvingSyncPipeline: boolean;
  /**
   * The notification says this event is waiting on the user. `/items/sync/status` reports a single
   * instance at a time and can fail outright for a brand with several connectors, so it is not a
   * dependable gate on its own. The notification is event-scoped and knows.
   */
  isPendingReview: boolean;
}

/**
 * Which sync pipeline produced `eventId`. Legacy is the answer whenever the notification says so or
 * is absent entirely, which keeps every brand that has not opted into ingest on the flow it has now.
 */
export const useIsIngestSync = (eventId?: string): IIngestSyncRouting => {
  const { data: notifications = [], isLoading } = useNotifications();

  const notification = useMemo(() => findProductSyncNotification(notifications, eventId), [notifications, eventId]);

  return {
    isIngestSync: isIngestSyncNotification(notification),
    isResolvingSyncPipeline: isLoading && !notification,
    isPendingReview: notification?.status === NotificationStatus.PENDING_REVIEW,
  };
};
